import type { CityConfig } from "../city-template/types";

export const fremontCaConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  lastModified: "2026-06-14",
  city: "Fremont",
  state: "California",
  stateAbbr: "CA",
  slug: "fremont-ca",
  route: "/fremont-ca",
  title: "Emergency Passport Fremont, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Fremont, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Alameda County and East Bay travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Fremont, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Fremont and Alameda County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Fremont%20California%20cityscape%20at%20golden%20hour%20with%20Mission%20Peak%20hills%20in%20the%20background%2C%20modern%20Silicon%20Valley%20suburban%20neighborhoods%20and%20tech%20campuses%2C%20Lake%20Elizabeth%20and%20Central%20Park%20view%20from%20above%2C%20warm%20sunset%20light%20over%20Alameda%20County%2C%20clean%20modern%20architecture%2C%20professional%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20East%20Bay%20suburban%20scene&width=1800&height=700&seq=fremont-ca-hero-001&orientation=landscape",
  heroImageAlt: "Fremont California cityscape at golden hour with Mission Peak and Lake Elizabeth",
  geoPosition: "37.5485;-121.9886",
  geoPlacename: "Fremont, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown SF — approximately 30–40 minutes from Fremont via I-880",
  },
  nearbyCitiesZips: [
    { name: "Fremont", zips: "94536–94539, 94555" },
    { name: "Newark", zips: "94560" },
    { name: "Union City", zips: "94587" },
    { name: "Hayward", zips: "94541–94545" },
    { name: "Milpitas", zips: "95035, 95036" },
    { name: "San Jose", zips: "95101–95103, 95110–95113, 95116, 95117, 95122–95126, 95128, 95131, 95132, 95134, 95136" },
    { name: "Pleasanton", zips: "94566, 94588" },
    { name: "Sunnyvale", zips: "94085–94090" },
    { name: "Santa Clara", zips: "95050–95056" },
    { name: "Palo Alto", zips: "94301–94306" },
    { name: "Menlo Park", zips: "94025–94029" },
    { name: "Redwood City", zips: "94061–94065" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Alameda County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Jose", route: "/san-jose" },
    { name: "San Francisco", route: "/san-francisco" },
    { name: "Oakland", route: "/oakland" },
    { name: "Berkeley", route: "/berkeley" },
    { name: "Santa Clara", route: "/santa-clara" },
    { name: "Sunnyvale", route: "/sunnyvale" },
    { name: "Hayward", route: "/hayward" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Fremont remotely via secure courier. We do not have a walk-in office in Fremont — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Alameda County travelers.",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  fpcOfficeDistance: "Serving all of Alameda County",
  serviceAreaIntro: "From the Warm Springs district to the Ardenwood area, from Niles to the Irvington neighborhood — Fast Passport Center serves the entire Fremont community. We regularly help travelers from Newark, Union City, Milpitas, and Hayward get their passports fast.",
  nearbyCitiesIntro: "We serve all Fremont neighborhoods and nearby East Bay and South Bay cities including Newark, Union City, Hayward, Milpitas, and San Jose. If you are in Alameda County or Santa Clara County, we have you covered.",
  howItWorksIntro: "Most Fremont travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With San Jose International Airport (SJC) and Oakland International Airport (OAK) both nearby, Fremont travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Fremont and Alameda County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Fremont? Here are the most common ones we hear from East Bay travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Fremont international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Raj P.",
      location: "Fremont, CA",
      rating: 5,
      text: "My flight to India was in 3 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Saved my entire trip and family visit.",
      service: "Emergency Passport",
    },
    {
      name: "Wei L.",
      location: "Newark, CA",
      rating: 5,
      text: "Needed to renew for a business trip to Shanghai. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend for East Bay tech professionals.",
      service: "Passport Renewal",
    },
    {
      name: "Maria G.",
      location: "Union City, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Italy. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "David K.",
      location: "Milpitas, CA",
      rating: 5,
      text: "As a frequent international business traveler from Silicon Valley, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "fremont-ca-1",
      name: "Fremont Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "37010 Dusterberry Way",
      city: "Fremont",
      state: "CA",
      zip: "94536",
      phone: "(510) 797-5419",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Fremont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "fremont-ca-2",
      name: "Newark Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "35900 Newark Blvd",
      city: "Newark",
      state: "CA",
      zip: "94560",
      phone: "(510) 793-4141",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Newark",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Newark and western Fremont residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "fremont-ca-3",
      name: "Union City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "31090 Dyer St",
      city: "Union City",
      state: "CA",
      zip: "94587",
      phone: "(510) 471-1215",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Union City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "fremont-ca-4",
      name: "Milpitas Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "455 S Milpitas Blvd",
      city: "Milpitas",
      state: "CA",
      zip: "95035",
      phone: "(408) 263-2500",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from the Great Mall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Fremont and Milpitas residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "fremont-ca-5",
      name: "Hayward Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2417 Industrial Pkwy",
      city: "Hayward",
      state: "CA",
      zip: "94545",
      phone: "(510) 786-0665",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Hayward",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for central Alameda County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "fremont-ca-6",
      name: "Alameda County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1106 Madison St",
      city: "Oakland",
      state: "CA",
      zip: "94607",
      phone: "(510) 272-6362",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "0.3 miles from the Oakland courthouse",
      website: "https://www.acgov.org/",
      websiteLabel: "Alameda County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "East Bay photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Alameda County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Fremont applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Alameda County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "East Bay travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Fremont." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Fremont address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Fremont before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Fremont for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most East Bay travelers." },
    { q: "How fast can I renew my passport in Fremont?", a: "Fast Passport Center offers fast passport renewal in Fremont in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my international trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Fremont in 24–48 hours so you don't miss your flights." },
    { q: "Can I get a same-day passport appointment near Fremont?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Fremont residents." },
    { q: "Can I expedite a child passport in Fremont?", a: "Yes. Fast Passport Center expedites child passports in Fremont for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Fremont with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Fremont?",
      a: "With Fast Passport Center's emergency courier service, Fremont travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Fremont?",
      a: "Popular options include the Fremont Post Office (94536), Newark Post Office (94560), Union City Post Office (94587), and Alameda County Clerk (94607). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Fremont?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live near the Tesla factory or in Warm Springs?",
      a: "We serve all of Fremont including Warm Springs, Ardenwood, Niles, Irvington, and Mission San Jose districts. Our overnight shipping labels work from any UPS location, so your location within Fremont doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Fremont?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Fremont?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Fremont, Newark, and Union City offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Fremont?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Fremont?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Alameda County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Fremont", address: "4020 Fremont Hub, Fremont, CA 94538", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/store-locator/cvs-pharmacy-locations" },
    { name: "Walgreens — Fremont", address: "39350 Fremont Blvd, Fremont, CA 94538", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/store-locator" },
    { name: "FedEx Office — Fremont", address: "43321 Boscell Rd, Fremont, CA 94538", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://local.fedex.com/" },
    { name: "UPS Store — Newark", address: "5600 Mowry School Rd, Newark, CA 94560", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/tools/find-a-store" },
    { name: "Walmart — Milpitas", address: "301 Ranch Dr, Milpitas, CA 95035", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/store/finder" },
    { name: "Rite Aid — Union City", address: "31824 Alvarado Blvd, Union City, CA 94587", hours: "Mon–Sun: 8AM–10PM", website: "https://www.riteaid.com/locations" },
    { name: "Postal Annex — Fremont", address: "46555 Mission Blvd, Fremont, CA 94539", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM", website: "https://www.postalannex.com/locations" },
    { name: "Costco — Fremont", address: "43600 Pacific Commons Blvd, Fremont, CA 94538", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/warehouse-locations" },
  ],
  dmvLocations: [
    {
      name: "DMV — Fremont",
      address: "4287 Central Ave, Fremont, CA 94536",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Hayward",
      address: "1500 W Winton Ave, Hayward, CA 94545",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — San Jose",
      address: "2222 Senter Rd, San Jose, CA 95112",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Oakland",
      address: "5300 Claremont Ave, Oakland, CA 94618",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Oakland and Berkeley residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "12 miles from Fremont",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "Convenient for South Bay travelers. Domestic and international flights to Mexico and beyond.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose Airport",
      featured: true,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "15 miles from Fremont",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Convenient for East Bay travelers. Major domestic hub with international connections.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "25 miles from Fremont",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and worldwide destinations.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Alameda County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Alameda County",
      url: "https://www.acgov.org/auditor/clerk/",
    },
    {
      label: "Alameda County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Alameda County",
      url: "https://www.alameda.courts.ca.gov/",
    },
  ],
};